import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTULOS_DA_RECOMENDACAO } from '@mpa/shared/recomendacoes';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { BlocoDeParametros, SIM_NAO, ehSim } from '../components/BlocoDeParametros';
import {
  Campo,
  Carregando,
  ErroBox,
  LinhaCampos,
  Modal,
  ModalRodape,
  PageHeader,
  Paginacao,
  Vazio,
} from '../components/ui';

interface Pessoa {
  id: string;
  jobTitle: string | null;
  company: string | null;
  person: { id: string; fullName: string; optOutAt: string | null };
}

interface Indicacao {
  id: string;
  status: string;
  score: number | null;
  band: string | null;
  justification: string | null;
  createdAt: string;
  approvedAt: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  target: Pessoa;
  suggested: Pessoa | null;
  rotulo: string;
  revisavel: boolean;
  reenviavel: boolean;
}

interface Motivo {
  id: string;
  kind: string;
  text: string;
  sequence: number;
}

interface Detalhe extends Indicacao {
  conversationStarters: string[] | null;
  attentionPoints: string[] | null;
  reasons: Motivo[];
}

interface Resumo {
  porEstado: Record<string, number>;
  total: number;
  pendentesDeRevisao: number;
  aprovadasNaoEnviadas: number;
  elegiveis: number;
  comIndicacao: number;
  conexoesPorParticipante: number;
  minScore: number;
  completudeMinima: number;
  enviaSemRevisao: boolean;
}

interface Recusa {
  participantId: string;
  nome: string;
  motivo: string;
}

/**
 * A cor diz o que fazer, não o que aconteceu — mesma regra da tela de jornadas.
 *
 * Amarelo é "alguém precisa olhar", azul é a plataforma trabalhando, verde é
 * desfecho bom, cinza é fim sem desfecho. Os rótulos vêm de `@mpa/shared`, que é
 * obrigado a cobrir o enum inteiro e tem teste disso: aqui ficam só as cores.
 */
const CORES: Record<string, string> = {
  GERADA: 'warning',
  AGUARDANDO_APROVACAO: 'warning',
  APROVADA: 'info',
  REJEITADA: 'muted',
  ENVIADA: 'info',
  ENTREGUE: 'info',
  VISUALIZADA: 'info',
  ACEITA: 'success',
  RECUSADA: 'muted',
  CONTATO_INICIADO: 'success',
  REUNIAO_REALIZADA: 'success',
  OPORTUNIDADE_IDENTIFICADA: 'success',
  FOLLOWUP_NECESSARIO: 'warning',
  SEM_RETORNO: 'muted',
};

/** Como o §13.4 nomeia os quatro tipos de motivo, para quem lê. */
const TIPOS_DE_MOTIVO: Record<string, { rotulo: string; cor: string }> = {
  interesse_comum: { rotulo: 'Interesse em comum', cor: 'info' },
  complementar: { rotulo: 'Complementares', cor: 'success' },
  oportunidade: { rotulo: 'Oportunidade', cor: 'success' },
  atencao: { rotulo: 'Atenção', cor: 'warning' },
};

const rotulo = (estado: string) =>
  ROTULOS_DA_RECOMENDACAO[estado as keyof typeof ROTULOS_DA_RECOMENDACAO] ?? estado;

const cargoEEmpresa = (p: Pessoa) =>
  [p.jobTitle, p.company].filter(Boolean).join(' · ') || '—';

/**
 * Revisão das indicações de conexão — a decisão A5 ganhando onde acontecer.
 *
 * ⚠️ **A justificativa aparece na LISTA, não escondida num modal.** O que esta
 * tela revisa é justamente esse texto: ele foi escrito por um modelo, vai chegar
 * a uma pessoa real na segunda pessoa do singular, e não tem como ser recolhido.
 * Uma tela de revisão que obrigue a abrir cada item para ver o que se está
 * aprovando convida a aprovar sem ler — e aí a revisão humana existe no papel e
 * não no fato.
 *
 * O modal acrescenta o que não cabe na linha: os motivos tipados, os assuntos
 * para começar e os pontos de atenção.
 */
export function Recomendacoes() {
  const { id: eventId } = useParams<{ id: string }>();
  const cliente = useQueryClient();
  const { pode } = useAuth();

  const [pagina, setPagina] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [soPendentes, setSoPendentes] = useState(true);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<'varrer' | 'encerrar' | null>(null);
  const [rejeitando, setRejeitando] = useState<Indicacao | null>(null);
  const [recusas, setRecusas] = useState<Recusa[] | null>(null);

  const busca = soPendentes && !filtro ? '&pendentes=true' : filtro ? `&status=${filtro}` : '';

  const consulta = useQuery({
    queryKey: ['recomendacoes', eventId, pagina, filtro, soPendentes],
    queryFn: () =>
      api<{
        data: Indicacao[];
        meta: { page: number; perPage: number; total: number; totalPages: number };
      }>(`/events/${eventId}/recommendations?page=${pagina}${busca}`),
  });

  const resumo = useQuery({
    queryKey: ['recomendacoes', eventId, 'resumo'],
    queryFn: () => api<Resumo>(`/events/${eventId}/recommendations/resumo`),
  });

  const detalhe = useQuery({
    queryKey: ['recomendacao', abrindo],
    queryFn: () => api<Detalhe>(`/recommendations/${abrindo}`),
    enabled: Boolean(abrindo),
  });

  const invalidar = () => cliente.invalidateQueries({ queryKey: ['recomendacoes', eventId] });

  const decidir = useMutation({
    mutationFn: ({ id, qual }: { id: string; qual: 'aprovar' | 'rejeitar' | 'reenviar' }) =>
      api(`/recommendations/${id}/${qual}`, { method: 'POST' }),
    onSuccess: () => {
      invalidar();
      setAbrindo(null);
      setRejeitando(null);
    },
  });

  const varrer = useMutation({
    mutationFn: () =>
      api<{ enfileiradas: number; recusadas: Recusa[]; foraDoCruzamento: number }>(
        `/events/${eventId}/recommendations/varrer`,
        { method: 'POST', body: { todos: true } },
      ),
    onSuccess: (resposta) => {
      invalidar();
      setConfirmando(null);
      if (resposta.recusadas.length) setRecusas(resposta.recusadas);
    },
  });

  const encerrar = useMutation({
    mutationFn: () =>
      api(`/events/${eventId}/recommendations/encerrar`, { method: 'POST' }),
    onSuccess: () => {
      invalidar();
      setConfirmando(null);
    },
  });

  const r = resumo.data;
  const semIndicacao = r ? Math.max(0, r.elegiveis - r.comIndicacao) : 0;

  return (
    <>
      <PageHeader
        titulo="Recomendações"
        voltar={{ para: `/eventos/${eventId}`, texto: 'Voltar ao evento' }}
        subtitulo="Cada indicação é uma pessoa apresentada a outra, com o motivo escrito."
        acoes={
          pode('recommendations.send') && (
            <>
              <button className="btn btn-secondary" onClick={() => setConfirmando('encerrar')}>
                <i className="ti ti-flag-check" /> Encerrar
              </button>
              <button className="btn btn-primary" onClick={() => setConfirmando('varrer')}>
                <i className="ti ti-affiliate" /> Buscar conexões
              </button>
            </>
          )
        }
      />

      {[decidir, varrer, encerrar].map((m, i) => m.isError && <ErroBox key={i} erro={m.error} />)}

      {r && (
        <>
          <div className="resumo-import" style={{ marginBottom: 18 }}>
            {Object.entries(r.porEstado)
              .sort(([, a], [, b]) => b - a)
              .map(([estado, quantos]) => (
                <button
                  key={estado}
                  className="resumo-item"
                  style={{
                    cursor: 'pointer',
                    borderColor: filtro === estado ? 'var(--accent)' : undefined,
                  }}
                  onClick={() => {
                    setFiltro(filtro === estado ? '' : estado);
                    setSoPendentes(false);
                    setPagina(1);
                  }}
                >
                  <div className="resumo-numero">{quantos}</div>
                  <div className="resumo-rotulo">{rotulo(estado)}</div>
                </button>
              ))}
          </div>

          {/*
            ⚠️ O aviso que evita dias de silêncio inexplicado.

            `APROVADA` depois de o envio rodar quer dizer uma coisa só: a mensagem
            não saiu. A etapa 8 sai dias depois da última interação, por desenho,
            com a janela de 24h fechada — e fora da janela o WhatsApp só entrega
            template aprovado. Sem este aviso, a organização descobriria o problema
            pela ausência de resposta dos participantes, uma semana depois.
          */}
          {r.aprovadasNaoEnviadas > 0 && (
            <div className="aviso aviso-danger" style={{ marginBottom: 16 }}>
              <i className="ti ti-send-off" />
              <span>
                <strong>{r.aprovadasNaoEnviadas}</strong> indicação(ões) aprovada(s) que{' '}
                <strong>não saíram</strong>. Fora da janela de 24h o WhatsApp só entrega template
                aprovado, e a etapa 8 sai dias depois da última conversa.{' '}
                {pode('templates.manage') ? (
                  <Link to="/templates">Conferir os templates de conexão</Link>
                ) : (
                  'Peça a quem cuida dos templates para submeter os modelos de conexão.'
                )}{' '}
                Depois disso, use “Reenviar” — nada foi perdido.
              </span>
            </div>
          )}

          {r.elegiveis === 0 ? (
            <div className="aviso" style={{ marginBottom: 16 }}>
              <i className="ti ti-user-question" />
              <span>
                Ninguém tem perfil suficiente para o cruzamento ainda. O perfil se forma na
                conversa da jornada.
              </span>
            </div>
          ) : (
            semIndicacao > 0 && (
              <div className="aviso" style={{ marginBottom: 16 }}>
                <i className="ti ti-users-plus" />
                <span>
                  <strong>{semIndicacao}</strong> de <strong>{r.elegiveis}</strong> pessoa(s) com
                  perfil suficiente ainda não têm nenhuma indicação. Cada uma recebe até{' '}
                  {r.conexoesPorParticipante}.
                </span>
              </div>
            )
          )}
        </>
      )}

      {/*
        O telefone não é decisão de quem revisa — e dizer isso aqui evita o erro.

        Quem escolhe se o número vai junto é a pessoa INDICADA, no consentimento
        da etapa 1 (decisão de 09/09/2026). Sem esta linha, quem aprova poderia
        achar que está liberando contato de alguém, e hesitar no que não é dela.
      */}
      <div className="aviso aviso-info" style={{ marginBottom: 16 }}>
        <i className="ti ti-phone" />
        <span>
          Aprovar não compartilha telefone. Quem decide se o número vai junto é a{' '}
          <strong>pessoa indicada</strong>, na autorização que ela deu ao entrar. Quem autorizou
          “sem telefone” tem o contato trocado só depois de os dois lados aceitarem.
        </span>
      </div>

      {pode('matching.rules_manage') && <ParametrosDoCruzamento eventId={eventId!} />}

      {r && r.total > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          <button
            className={`btn btn-sm ${soPendentes && !filtro ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setSoPendentes(true);
              setFiltro('');
              setPagina(1);
            }}
          >
            Esperando revisão ({r.pendentesDeRevisao})
          </button>
          <button
            className={`btn btn-sm ${!soPendentes && !filtro ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setSoPendentes(false);
              setFiltro('');
              setPagina(1);
            }}
          >
            Todas ({r.total})
          </button>
        </div>
      )}

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio
            icone="ti-affiliate"
            titulo={
              filtro
                ? `Nenhuma indicação ${rotulo(filtro).toLowerCase()}`
                : soPendentes && r?.total
                  ? 'Nada esperando revisão'
                  : 'Nenhuma indicação ainda'
            }
            descricao={
              filtro || (soPendentes && r?.total)
                ? 'O filtro continua ligado — as outras indicações continuam existindo.'
                : 'As indicações nascem de uma busca de conexões. Até alguém buscar, ninguém é apresentado a ninguém.'
            }
            acao={
              filtro || (soPendentes && r?.total) ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setFiltro('');
                    setSoPendentes(false);
                    setPagina(1);
                  }}
                >
                  Ver todas as indicações
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Para</th>
                    <th>Apresentar</th>
                    <th>O que a pessoa vai ler</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((ind) => (
                    <tr key={ind.id}>
                      <td>
                        <div className="celula-principal">{ind.target.person.fullName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {cargoEEmpresa(ind.target)}
                        </div>
                      </td>

                      <td>
                        {ind.suggested ? (
                          <>
                            <div className="celula-principal">
                              {ind.suggested.person.fullName}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              {cargoEEmpresa(ind.suggested)}
                            </div>
                            {ind.suggested.person.optOutAt && (
                              <Tag type="danger">Saiu da lista</Tag>
                            )}
                          </>
                        ) : (
                          <span style={{ color: 'var(--text-dim)' }}>—</span>
                        )}
                      </td>

                      <td style={{ maxWidth: 420 }}>
                        <button
                          onClick={() => setAbrindo(ind.id)}
                          title="Ver os motivos e os assuntos para começar"
                          style={{
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            color: 'inherit',
                            cursor: 'pointer',
                          }}
                        >
                          {ind.justification || (
                            /*
                              Score sem justificativa é DEFEITO, não dado
                              incompleto — `ARQUITETURA.md §13.4` proíbe, e o
                              schema Zod do estágio 2 exige o texto. Se aparecer
                              aqui, algo escreveu em `score` por fora.
                            */
                            <span style={{ color: 'var(--danger)' }}>
                              Sem justificativa — não envie, isto é um defeito.
                            </span>
                          )}
                        </button>
                        {ind.score !== null && (
                          <div style={{ marginTop: 6 }}>
                            <Tag type="muted" title={ind.band ?? undefined}>
                              {ind.score}/100
                            </Tag>
                          </div>
                        )}
                      </td>

                      <td>
                        <Tag type={CORES[ind.status] ?? 'muted'}>{ind.rotulo}</Tag>
                        {ind.sentAt && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            {new Date(ind.sentAt).toLocaleString('pt-BR')}
                          </div>
                        )}
                      </td>

                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {ind.revisavel && pode('recommendations.approve') && (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                disabled={!ind.justification || decidir.isPending}
                                title={
                                  ind.justification
                                    ? 'Aprovar e enviar'
                                    : 'Sem justificativa não se envia'
                                }
                                onClick={() => decidir.mutate({ id: ind.id, qual: 'aprovar' })}
                              >
                                Aprovar
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setRejeitando(ind)}
                              >
                                Rejeitar
                              </button>
                            </>
                          )}
                          {ind.reenviavel && pode('recommendations.send') && (
                            <button
                              className="btn btn-secondary btn-sm"
                              title="Tentar a entrega de novo"
                              onClick={() => decidir.mutate({ id: ind.id, qual: 'reenviar' })}
                            >
                              Reenviar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ padding: 12 }}>
              <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
            </div>
          </div>
        ))}

      {abrindo && (
        <Modal
          titulo="A indicação inteira"
          descricao="O que o cruzamento encontrou, e o que vai ser dito."
          aoFechar={() => setAbrindo(null)}
          largura={640}
        >
          {detalhe.isPending && <Carregando />}
          {detalhe.isError && <ErroBox erro={detalhe.error} />}

          {detalhe.data && (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>{detalhe.data.target.person.fullName}</strong> recebe a apresentação de{' '}
                <strong>{detalhe.data.suggested?.person.fullName ?? '—'}</strong>.
              </p>

              <div className="card" style={{ padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                  O texto que a pessoa vai ler
                </div>
                {detalhe.data.justification ?? '—'}
              </div>

              {detalhe.data.reasons.length > 0 && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Por quê
                  </div>
                  <ul style={{ margin: '0 0 16px', paddingLeft: 18 }}>
                    {detalhe.data.reasons.map((m) => (
                      <li key={m.id} style={{ marginBottom: 6 }}>
                        <Tag type={TIPOS_DE_MOTIVO[m.kind]?.cor ?? 'muted'}>
                          {TIPOS_DE_MOTIVO[m.kind]?.rotulo ?? m.kind}
                        </Tag>{' '}
                        {m.text}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {detalhe.data.conversationStarters?.length ? (
                <>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Assuntos para começar
                  </div>
                  <ul style={{ margin: '0 0 16px', paddingLeft: 18 }}>
                    {detalhe.data.conversationStarters.map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </>
              ) : null}

              {detalhe.data.attentionPoints?.length ? (
                <div className="aviso" style={{ marginBottom: 16 }}>
                  <i className="ti ti-alert-triangle" />
                  <span>{detalhe.data.attentionPoints.join(' · ')}</span>
                </div>
              ) : null}

              <ModalRodape>
                <button className="btn btn-secondary" onClick={() => setAbrindo(null)}>
                  Fechar
                </button>
                {detalhe.data.revisavel && pode('recommendations.approve') && (
                  <>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setRejeitando(detalhe.data);
                        setAbrindo(null);
                      }}
                    >
                      Rejeitar
                    </button>
                    <button
                      className="btn btn-primary"
                      disabled={!detalhe.data.justification || decidir.isPending}
                      onClick={() => decidir.mutate({ id: detalhe.data.id, qual: 'aprovar' })}
                    >
                      Aprovar e enviar
                    </button>
                  </>
                )}
              </ModalRodape>
            </>
          )}
        </Modal>
      )}

      {/*
        Rejeitar pede confirmação porque o efeito é definitivo de um jeito que o
        nome não entrega: além de a indicação não sair, o PAR fica queimado — o
        cruzamento não voltará a oferecer essas duas pessoas uma à outra, em
        nenhuma rodada. Gerar de novo desfaria a revisão de quem clicou.
      */}
      {rejeitando && (
        <Modal
          titulo="Rejeitar a indicação"
          aoFechar={() => setRejeitando(null)}
          largura={520}
        >
          <p style={{ marginTop: 0 }}>
            <strong>{rejeitando.target.person.fullName}</strong> não será apresentado a{' '}
            <strong>{rejeitando.suggested?.person.fullName ?? '—'}</strong>, e nunca saberá que
            esta indicação existiu.
          </p>
          <div className="aviso" style={{ marginBottom: 16 }}>
            <i className="ti ti-alert-triangle" />
            <span>
              Este par <strong>não volta</strong> em nenhuma rodada seguinte. Não gasta uma das{' '}
              {resumo.data?.conexoesPorParticipante ?? 5} indicações da pessoa.
            </span>
          </div>
          <ModalRodape>
            <button className="btn btn-secondary" onClick={() => setRejeitando(null)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={decidir.isPending}
              onClick={() => decidir.mutate({ id: rejeitando.id, qual: 'rejeitar' })}
            >
              Rejeitar
            </button>
          </ModalRodape>
        </Modal>
      )}

      {confirmando === 'varrer' && (
        <Modal
          titulo="Buscar conexões"
          descricao="Vale para todos os participantes com perfil suficiente."
          aoFechar={() => setConfirmando(null)}
          largura={560}
        >
          <div className="aviso" style={{ marginBottom: 16 }}>
            <i className="ti ti-alert-triangle" />
            {/* Até 14/10/2026 dizia "uma chamada de IA por pessoa" e "avisa quem tiver
                indicação nova" nos dois modos — e com a revisão a busca não avisa
                ninguém: quem avisa é a aprovação. */}
            {resumo.data?.enviaSemRevisao ? (
              <span>
                Isto gasta chamadas de IA — até três por pessoa, só para os pares ainda não
                avaliados. Este evento <strong>envia sem revisão</strong>: cada indicação nova
                sai na hora pelo WhatsApp, com o aviso de quantas a pessoa já recebeu.
              </span>
            ) : (
              <span>
                Isto gasta chamadas de IA — até três por pessoa, só para os pares ainda não
                avaliados. As indicações nascem <strong>aguardando a sua aprovação</strong> — nada é
                apresentado a ninguém antes disso. Alguns minutos depois da última indicação que
                você aprovar para uma pessoa, ela recebe o aviso de quantas já recebeu.
              </span>
            )}
          </div>
          <p style={{ color: 'var(--text-muted)' }}>
            Cada rodada busca só o que falta para completar{' '}
            {resumo.data?.conexoesPorParticipante ?? 5} por pessoa, e nunca repete quem já foi
            indicado. Pode rodar de novo quando mais gente terminar a conversa.
          </p>
          {/* O corte explica a pergunta seguinte — "por que só apareceram duas?" —
              antes de ela ser feita, e diz onde se muda. */}
          <p style={{ color: 'var(--text-muted)' }}>
            Pares com score abaixo de <strong>{resumo.data?.minScore ?? 60}</strong> não viram
            indicação. É melhor indicar menos e com motivo — o corte está em “Regras do
            cruzamento”.
          </p>
          <ModalRodape>
            <button className="btn btn-secondary" onClick={() => setConfirmando(null)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={varrer.isPending}
              onClick={() => varrer.mutate()}
            >
              {varrer.isPending ? 'Enfileirando…' : 'Buscar conexões'}
            </button>
          </ModalRodape>
        </Modal>
      )}

      {confirmando === 'encerrar' && (
        <Modal
          titulo="Encerrar o cruzamento"
          aoFechar={() => setConfirmando(null)}
          largura={560}
        >
          <div className="aviso" style={{ marginBottom: 16 }}>
            <i className="ti ti-alert-triangle" />
            <span>
              Quem ficou com menos de {resumo.data?.conexoesPorParticipante ?? 5} indicações recebe
              uma mensagem dizendo <strong>quantas</strong> foram encontradas — e a plataforma para
              de prometer mais.
            </span>
          </div>
          <p style={{ color: 'var(--text-muted)' }}>
            Faça isto quando o evento acabar. Antes disso, uma nova busca ainda pode completar a
            lista de quem está incompleto.
          </p>
          <ModalRodape>
            <button className="btn btn-secondary" onClick={() => setConfirmando(null)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={encerrar.isPending}
              onClick={() => encerrar.mutate()}
            >
              Encerrar e avisar
            </button>
          </ModalRodape>
        </Modal>
      )}

      {/*
        Quem ficou de fora aparece com o motivo, pessoa por pessoa — mesmo
        formato da importação e do disparo de jornadas. "Nenhuma conexão
        encontrada" não diz a ninguém que o problema era perfil raso.
      */}
      {recusas && (
        <Modal
          titulo="Quem ficou de fora"
          descricao="A busca rodou para o resto."
          aoFechar={() => setRecusas(null)}
          largura={560}
        >
          <table className="data-table">
            <tbody>
              {recusas.map((r) => (
                <tr key={r.participantId}>
                  <td>{r.nome}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.motivo}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ModalRodape>
            <button className="btn btn-primary" onClick={() => setRecusas(null)}>
              Entendi
            </button>
          </ModalRodape>
        </Modal>
      )}
    </>
  );
}

/**
 * As regras do cruzamento deste evento.
 *
 * ⚠️ **`autoSendRecommendations` é a decisão A5 virando botão.** Ela existia no
 * schema desde o começo, nascendo `false`, e só se mudava no banco: ou seja, a
 * revisão humana era obrigatória por falta de alternativa, não por escolha. Com o
 * controle aqui, a escolha passa a ser da organização — e por isso a tela diz, em
 * letras, o que se perde ao desligar.
 *
 * Pede `matching.rules_manage` e não `recommendations.approve`: são as regras do
 * cruzamento, e é a permissão que impede quem apenas agenda disparos de desligar
 * a revisão de indicações.
 */
function ParametrosDoCruzamento({ eventId }: { eventId: string }) {
  return (
    <BlocoDeParametros
      eventId={eventId}
      titulo="Regras do cruzamento"
      icone="ti-adjustments-alt"
      caminho="/matchmaking"
      corpo={(dados) => ({
        minScore: dados.minScore,
        maxRecommendationsPerParticipant: dados.maxRecommendationsPerParticipant,
        // Booleano de verdade, e não a string do formulário: a API recusa
        // `"false"` de propósito, porque `Boolean('false')` é `true`.
        blockSameCompany: ehSim(dados.blockSameCompany),
        autoSendRecommendations: ehSim(dados.autoSendRecommendations),
      })}
      rodape={
        'Ao salvar, as indicações se ajustam sozinhas: as que esperam aprovação e não cabem ' +
        'mais na regra saem, e as que passaram a caber são geradas (com IA só para os pares ' +
        'que ainda não têm texto). Aprovadas e enviadas não mudam.'
      }
    >
      {({ settings: s }) => (
        <>
          <LinhaCampos>
            <Campo
              label="Score mínimo"
              name="minScore"
              tipo="number"
              valorInicial={String(s?.minScore ?? 60)}
              ajuda="De 0 a 100. Abaixo disso o par não vira indicação — melhor indicar menos."
            />
            <Campo
              label="Indicações por pessoa"
              name="maxRecommendationsPerParticipant"
              tipo="number"
              valorInicial={String(s?.maxRecommendationsPerParticipant ?? 5)}
              ajuda="Cinco é o combinado com o cliente. Cada uma é uma pessoa para conhecer."
            />
          </LinhaCampos>

          <LinhaCampos>
            <Campo
              label="Evitar colegas da mesma empresa"
              name="blockSameCompany"
              valorInicial={s?.blockSameCompany === false ? 'nao' : 'sim'}
              opcoes={SIM_NAO}
              ajuda="Quem já almoça junto não precisa de apresentação."
            />
            <Campo
              label="Enviar sem revisão humana"
              name="autoSendRecommendations"
              valorInicial={s?.autoSendRecommendations ? 'sim' : 'nao'}
              opcoes={SIM_NAO}
              ajuda={
                'Com “Sim”, a indicação vai direto para a pessoa — ninguém lê o texto antes. ' +
                'As que já esperam aprovação continuam esperando.'
              }
            />
          </LinhaCampos>

          {s?.autoSendRecommendations && (
            <div className="aviso" style={{ marginTop: 12 }}>
              <i className="ti ti-alert-triangle" />
              <span>
                A revisão humana está <strong>desligada</strong>. O texto que a IA escreve sobre
                uma pessoa chega à outra sem ninguém ler, e mensagem enviada não se recolhe.
              </span>
            </div>
          )}
        </>
      )}
    </BlocoDeParametros>
  );
}
